import React from 'react';
import { useTranslation } from 'react-i18next';
import { Gauge, Fuel, Droplets, Info, Lock } from 'lucide-react';

/**
 * StepResources - Step 7 of VehicleLifecycleWorkflow
 *
 * Captures baseline readings so the system doesn't start with blank data:
 * - Odometer (mandatory for TCO, maintenance scheduling, lease tracking)
 * - Fuel level (optional, avoids false "low fuel" alerts)
 * - AdBlue level (optional, for diesel vehicles)
 *
 * En mode édition, tous les champs sont gelés : ce sont des relevés initiaux
 * historiques, on ne réécrit pas l'historique du véhicule.
 */
function StepResources({ formData, updateFormData, isEditMode = false }) {
  const { t } = useTranslation();
  const isDiesel = (formData.fuel_type || '').toLowerCase() === 'diesel';
  const isElectric = (formData.fuel_type || '').toLowerCase() === 'electric';
  const locked = isEditMode;
  const noop = () => {};

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">{t('vehicles.wizard.resources.title')}</h3>

      {locked ? (
        <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Lock className="w-6 h-6 text-gray-600 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">{t('vehicles.wizard.resources.lockedTitle')}</h4>
              <p className="text-sm text-gray-700">
                {t('vehicles.wizard.resources.lockedHint')}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Gauge className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-teal-900 mb-2">{t('vehicles.wizard.resources.introTitle')}</h4>
              <p className="text-sm text-teal-800">
                {t('vehicles.wizard.resources.introHint')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Odometer — the critical one */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Gauge className="w-4 h-4 inline mr-1" />
          {t('vehicles.wizard.resources.odometerLabel')}
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            step="1"
            value={formData.initial_odometer_km || ''}
            onChange={locked ? noop : (e) => updateFormData('initial_odometer_km', e.target.value ? parseInt(e.target.value) : '')}
            readOnly={locked}
            disabled={locked}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${locked ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed' : 'border-gray-300'}`}
            placeholder="Ex: 45230"
          />
          <span className="text-sm text-gray-500 whitespace-nowrap">{t('vehicles.wizard.resources.km')}</span>
        </div>
        {!locked && (
          <p className="text-sm text-gray-500 mt-1">
            {t('vehicles.wizard.resources.odometerHint')}
          </p>
        )}
        {!locked && !formData.initial_odometer_km && (
          <div className="flex items-center gap-1.5 mt-2 text-amber-600 text-xs">
            <Info className="w-3.5 h-3.5" />
            {t('vehicles.wizard.resources.odometerWarning')}
          </div>
        )}
      </div>

      {/* Fuel level */}
      {!isElectric && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Fuel className="w-4 h-4 inline mr-1" />
            {t('vehicles.wizard.resources.fuelLevelLabel')}
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={formData.initial_fuel_level || 50}
              onChange={locked ? noop : (e) => updateFormData('initial_fuel_level', parseInt(e.target.value))}
              disabled={locked}
              className={`flex-1 h-2 bg-gray-200 rounded-lg appearance-none accent-amber-500 ${locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            />
            <span className="text-sm font-medium text-gray-700 w-12 text-right">
              {formData.initial_fuel_level || 50} %
            </span>
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>{t('vehicles.wizard.resources.levelEmpty')}</span>
            <span>{t('vehicles.wizard.resources.levelQuarter')}</span>
            <span>{t('vehicles.wizard.resources.levelHalf')}</span>
            <span>{t('vehicles.wizard.resources.levelThreeQuarters')}</span>
            <span>{t('vehicles.wizard.resources.levelFull')}</span>
          </div>
        </div>
      )}

      {/* Battery level for electric */}
      {isElectric && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Fuel className="w-4 h-4 inline mr-1" />
            {t('vehicles.wizard.resources.batteryLevelLabel')}
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={formData.initial_fuel_level || 80}
              onChange={locked ? noop : (e) => updateFormData('initial_fuel_level', parseInt(e.target.value))}
              disabled={locked}
              className={`flex-1 h-2 bg-gray-200 rounded-lg appearance-none accent-green-500 ${locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            />
            <span className="text-sm font-medium text-gray-700 w-12 text-right">
              {formData.initial_fuel_level || 80} %
            </span>
          </div>
        </div>
      )}

      {/* AdBlue for diesel */}
      {isDiesel && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Droplets className="w-4 h-4 inline mr-1" />
            {t('vehicles.wizard.resources.adblueLevelLabel')}
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={formData.initial_adblue_level || 50}
              onChange={locked ? noop : (e) => updateFormData('initial_adblue_level', parseInt(e.target.value))}
              disabled={locked}
              className={`flex-1 h-2 bg-gray-200 rounded-lg appearance-none accent-blue-500 ${locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            />
            <span className="text-sm font-medium text-gray-700 w-12 text-right">
              {formData.initial_adblue_level || 50} %
            </span>
          </div>
        </div>
      )}

      {/* Summary card — création uniquement */}
      {!locked && (
        <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
          <p className="font-medium text-gray-700 mb-2">{t('vehicles.wizard.resources.summaryTitle')}</p>
          <ul className="space-y-1 ml-4 list-disc">
            <li>{t('vehicles.wizard.resources.summaryOdometer')}</li>
            <li>{t('vehicles.wizard.resources.summaryFluids')}</li>
            <li>{t('vehicles.wizard.resources.summaryAlerts')}</li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default StepResources;
